import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { Footer, Header } from './headerFooter';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import { getHost } from './config';
import FilterSidebar from './FilterSidebar';
import { initialSidebarOpen, collapseFilterSidebarOnMobile, joinFilterSummaryParts } from './filterSidebarUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import './custom.scss';

const locationSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '42px',
        boxShadow: state.isFocused
            ? '0 0 0 2px rgba(80, 137, 162, 0.45)'
            : '0 4px 8px rgba(0, 0, 0, 0.15)',
        borderColor: state.isFocused
            ? '#5089a2'
            : 'var(--bs-border-color, #dee2e6)',
        cursor: 'text',
    }),
    valueContainer: (base) => ({
        ...base,
        flexWrap: 'nowrap',
        overflowX: 'hidden',
        paddingTop: 4,
        paddingBottom: 4,
        gap: 4,
    }),
    // Hide internal chips so selections are shown in custom area below
    multiValue: (base) => ({ ...base, display: 'none' }),
    multiValueLabel: (base) => ({ ...base, display: 'none' }),
    multiValueRemove: (base) => ({ ...base, display: 'none' }),
    menu: (base) => ({ ...base, zIndex: 5 }),
    option: (base, state) => ({
        ...base,
        fontSize: '0.9rem',
        backgroundColor: state.isSelected
            ? '#5089a2'
            : state.isFocused
                ? 'rgba(80, 137, 162, 0.12)'
                : 'white',
        color: state.isSelected ? 'white' : 'inherit',
    }),
};

function formatScore(value) {
    return value === null || value === undefined ? '-' : value;
}

function AppointmentsMinTable() {
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');

    const [specialty, setSpecialty] = useState('');
    const [specialtyOptions, setSpecialtyOptions] = useState([]);
    const [specialtyLoading, setSpecialtyLoading] = useState(false); // Loading state for specialty fetch
    const [placesOptions, setPlacesOptions] = useState([]);
    const [placesLoading, setPlacesLoading] = useState(false); // Loading state for places fetch
    const [locations, setLocations] = useState([]);
    const [year, setYear] = useState('all');
    const [yearOptions, setYearOptions] = useState([]);
    const [yearOptionsLoading, setYearOptionsLoading] = useState(false); // Loading state for year options fetch
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
        }
    }, [token, navigate]);

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());
        navigate('/', { replace: true });
    };

    useEffect(() => {
        setSpecialtyLoading(true);
        const url = `${getHost()}/api/specialty-types/appointments`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(response => response.json())
            .then(data => setSpecialtyOptions(data.results || []))
            .catch(error => console.error('Error fetching specialties:', error))
            .finally(() => setSpecialtyLoading(false));
    }, [token]);

    useEffect(() => {
        setYearOptionsLoading(true);
        const url = `${getHost()}/api/appointments-years`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(response => response.json())
            .then(data => setYearOptions(data.results || []))
            .catch(error => console.error('Error fetching years:', error))
            .finally(() => setYearOptionsLoading(false));
    }, [token]);

    useEffect(() => {
        if (specialty) {
            setPlacesLoading(true);
            const url = `${getHost()}/api/places/appointments?specialty=${encodeURIComponent(specialty)}`;
            fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            })
                .then(response => response.json())
                .then(data => setPlacesOptions(data.results || []))
                .catch(error => console.error('Error fetching places:', error))
                .finally(() => setPlacesLoading(false));
        } else {
            setPlacesOptions([]);
            setLocations([]);
        }
    }, [specialty, token]);

    const placeOptions = useMemo(
        () =>
            placesOptions
                .slice()
                .sort((a, b) => a.localeCompare(b, 'el'))
                .map((place) => ({ value: place, label: place })),
        [placesOptions]
    );

    const locationsValue = useMemo(
        () => locations.map((loc) => ({ value: loc, label: loc })),
        [locations]
    );

    const handleLocationChange = (selected) => {
        setLocations(selected ? selected.map((s) => s.value) : []);
    };

    const handleSubmit = () => {
        setLoading(true);
        const url = `${getHost()}/api/appointments-min-table`;
        const requestBody = {
            specialty: specialty,
            locations: locations,
            year: year === 'all' ? null : parseInt(year, 10),
        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
        })
            .then(response => response.json())
            .then(data => {
                setData(data.results);
                collapseFilterSidebarOnMobile(setSidebarOpen);
            })
            .catch(error => console.error('Error fetching data:', error))
            .finally(() => {
                setLoading(false);
            });
    };

    const isFormValid = specialty !== '' && locations.length > 0;

    const filterSummary = useMemo(() => {
        const count = locations.length;
        return joinFilterSummaryParts([
            specialty,
            count > 0 ? `${count} ${count === 1 ? 'περιοχή' : 'περιοχές'}` : '',
            year === 'all' ? 'Όλα τα έτη' : year,
        ]);
    }, [specialty, locations, year]);

    return (
        <div id="root">
            <Header handleLogout={handleLogout} />
            <main className='main-container ranking-per-state-page'>
                <FilterSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    filterSummary={filterSummary}
                    bodyId="appointments-min-table-sidebar-body"
                >
                    <Form>
                        <Form.Group className="mb-3 pt-3" controlId="appointmentsMinTable.specialty">
                            <Form.Label className='custom-label'>Κλάδος Διορισμού:</Form.Label>
                            <Form.Select
                                className="custom-form"
                                value={specialty}
                                onChange={(e) => {
                                    setSpecialty(e.target.value);
                                    setLocations([]);
                                    setData(null);
                                }}
                                disabled={specialtyLoading}
                            >
                                <option value="">{specialtyLoading ? 'Φόρτωση...' : 'Επιλέξτε...'}</option>
                                {!specialtyLoading && specialtyOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="appointmentsMinTable.locations">
                            <Form.Label className='custom-label'>Περιοχή Διορισμού:</Form.Label>
                            {specialty && locations.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
                                    <Form.Text className="text-muted">
                                        {locations.length} επιλεγμένες
                                    </Form.Text>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        type="button"
                                        className="rounded-pill"
                                        style={{
                                            width: 'auto',
                                            whiteSpace: 'nowrap',
                                            flex: '0 0 auto',
                                            paddingLeft: '5px',
                                            paddingRight: '5px',
                                        }}
                                        onClick={() => setLocations([])}
                                    >
                                        Καθαρισμός επιλογών
                                    </Button>
                                </div>
                            )}
                            <Select
                                isMulti
                                placeholder={placesLoading ? 'Φόρτωση...' : 'Αναζήτηση και επιλογή περιοχών...'}
                                loadingMessage={() => 'Φόρτωση...'}
                                noOptionsMessage={() => 'Δεν βρέθηκαν περιοχές'}
                                options={placeOptions}
                                value={locationsValue}
                                onChange={handleLocationChange}
                                isDisabled={!specialty || placesLoading}
                                isLoading={placesLoading}
                                maxMenuHeight={280}
                                closeMenuOnSelect={false}
                                blurInputOnSelect={false}
                                hideSelectedOptions={false}
                                styles={locationSelectStyles}
                                className="predict-location-select"
                                classNamePrefix="predict-location"
                            />
                            {locations.length === 0 && specialty && (
                                <Form.Text className="text-muted d-block mt-1">
                                    Δεν έχουν επιλεγεί περιοχές. Πληκτρολογήστε για να αναζητήσετε και να προσθέσετε περιοχές από τη λίστα.
                                </Form.Text>
                            )}
                            {locations.length > 0 && (
                                <div className="mt-2 d-flex flex-wrap gap-2">
                                    {locations.map((loc) => (
                                        <span
                                            key={loc}
                                            className="badge rounded-pill text-bg-light border px-2 py-1 d-flex align-items-center gap-1"
                                        >
                                            <span>{loc}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link p-0 text-muted"
                                                onClick={() =>
                                                    setLocations((prev) => prev.filter((l) => l !== loc))
                                                }
                                                aria-label={`Αφαίρεση ${loc}`}
                                            >
                                                <i className="bi bi-x" aria-hidden="true" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="appointmentsMinTable.year">
                            <Form.Label className='custom-label'>Έτος:</Form.Label>
                            <Form.Select
                                className="custom-form"
                                value={year}
                                onChange={(e) => {
                                    setYear(e.target.value);
                                    setData(null);
                                }}
                                disabled={yearOptionsLoading}
                            >
                                <option value="all">{yearOptionsLoading ? 'Φόρτωση...' : 'Όλα τα έτη'}</option>
                                {!yearOptionsLoading && yearOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                    <div className="d-flex justify-content-center mt-3">
                        <Button
                            variant="secondary"
                            type="button"
                            className="predict-submit-button"
                            onClick={handleSubmit}
                            disabled={!isFormValid}
                        >
                            Υποβολή
                        </Button>
                    </div>
                </FilterSidebar>
                <div className={`ranking-per-state-main ${!sidebarOpen ? 'ranking-per-state-main--sidebar-collapsed' : ''}`}>
                    <Container fluid className="ranking-per-state-container">
                        <Row className="g-3 g-md-4">
                            <Col xs={12}>
                                <div className="ranking-per-state-content">
                                    {!data && !loading && (
                                        <p className="text-muted small mb-2">
                                            Επιλέγοντας τον <b>Κλάδο Διορισμού</b> και μία ή περισσότερες <b>Περιοχές Διορισμού</b>,
                                            δείτε ανά περιοχή τον αριθμό διορισμών ανά έτος, καθώς και τη μέγιστη (τελευταίος διορισθείς)
                                            <b> Σειρά Πίνακα</b> και τα αντίστοιχα <b>Μόρια Πίνακα</b> για κάθε έτος.
                                        </p>
                                    )}
                                    {loading && (
                                        <div className="text-center mt-3 mb-3">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div>Φόρτωση αποτελεσμάτων...</div>
                                        </div>
                                    )}

                                    {data ? (
                                        <div className={!sidebarOpen ? 'results-snug-mobile' : ''}>
                                            <div className="d-flex align-items-center my-3">
                                                <h5 className="text-center flex-grow-1 m-0">Αποτελέσματα</h5>
                                            </div>
                                            {data.length === 0 ? (
                                                <p className="text-muted small mb-2">Δεν βρέθηκαν αποτελέσματα.</p>
                                            ) : (
                                                <div className="data-container">
                                                    {data.map((placeData) => {
                                                        const place = Object.keys(placeData)[0];
                                                        const years = placeData[place];
                                                        return (
                                                            <Card className="custom-card" key={place}>
                                                                <Card.Body>
                                                                    <Card.Title>{place}</Card.Title>
                                                                    {Object.entries(years).map(([entryYear, info]) => (
                                                                        <ListGroup className="list-group-flush mb-3" key={entryYear}>
                                                                            <ListGroup.Item className="fw-semibold">{entryYear}</ListGroup.Item>
                                                                            <ListGroup.Item>Διορισμοί: {info.total}</ListGroup.Item>
                                                                            <ListGroup.Item>Μέγιστη Σειρά Πίνακα: {formatScore(info.table_max)}</ListGroup.Item>
                                                                            <ListGroup.Item>Μόρια Πίνακα: {formatScore(info.table_max_score)}</ListGroup.Item>
                                                                            <ListGroup.Item>Πίνακας ΑΣΕΠ: {formatScore(info.asep_table)}</ListGroup.Item>
                                                                        </ListGroup>
                                                                    ))}
                                                                </Card.Body>
                                                            </Card>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ) : !loading && null}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default AppointmentsMinTable;